import { useState, useEffect } from 'react';
import { useAuth } from '../../../app/providers/AuthContext';
import { apiRequest } from '../../../shared/api/base';

/**
 * Hook to fetch and check user's course entitlements
 *
 * Usage:
 *   const { courseCodes, hasAccess, loading } = useEntitlements();
 *   if (hasAccess('phonics')) { ... }
 */
export function useEntitlements() {
  const { user } = useAuth();
  const [entitlements, setEntitlements] = useState({
    courseCodes: [],
    details: [],
    hasFullAccess: false,
    loading: true,
    error: null
  });

  useEffect(() => {
    // User not logged in - no entitlements
    if (!user) {
      setEntitlements({
        courseCodes: [],
        details: [],
        hasFullAccess: false,
        loading: false,
        error: null
      });
      return;
    }

    // Fetch user's entitlements from backend
    setEntitlements(prev => ({ ...prev, loading: true }));

    apiRequest('/entitlements/me')
      .then(data => {
        setEntitlements({
          courseCodes: data.courseCodes || [],
          details: data.details || [],
          hasFullAccess: data.hasFullAccess || false,
          loading: false,
          error: null
        });
      })
      .catch(err => {
        console.error('❌ Failed to fetch entitlements:', err);
        setEntitlements({
          courseCodes: [],
          details: [],
          hasFullAccess: false,
          loading: false,
          error: err.message || 'Failed to load entitlements'
        });
      });
  }, [user]);

  function hasAccess(courseCode) {
    if (!courseCode) return false;
    return entitlements.courseCodes.includes(courseCode.toLowerCase());
  }

  return {
    ...entitlements,
    hasAccess
  };
}
